import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, RotateCw, Server, Undo2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useServerLogsQuery, useServerOpMutation, useServerStatusQuery } from '@/client/api/whatsapp/line-actions';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog, ErrorNote, Loading, selectCls, toastError } from './shared';

const TAG = /^\d+\.\d+\.\d+(-rc\d+)?$/;
type Op = 'restart' | 'upgrade' | 'rollback';

const uptime = (s?: number) => {
  if (!s) return 'unknown';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d ? `${d}d ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`;
};

const ServerPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const status = useServerStatusQuery(open);
  const [service, setService] = useState<'api' | 'redis'>('api');
  const [tail, setTail] = useState(200);
  const logs = useServerLogsQuery(service, tail, open);
  const op = useServerOpMutation();
  const [tag, setTag] = useState('');
  const [ask, setAsk] = useState<Op | null>(null);
  const box = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [logs.data]);

  const s = status.data?.data;
  const h = s?.host;
  const tagOk = TAG.test(tag.trim());
  const load = Array.isArray(h?.load) ? h!.load.join(' / ') : h?.load;

  const confirmCopy: Record<Op, { title: string; body: string; action: string }> = {
    restart: { title: 'Restart the Evolution API?', body: 'The API container restarts. Linked phones disconnect and reconnect on their own, which takes up to a minute. Sends in that window fail.', action: 'Restart' },
    upgrade: { title: `Upgrade to ${tag.trim()}?`, body: `The server pulls evoapicloud/evolution-api:${tag.trim()} and restarts the API, so linked phones reconnect. If the new version does not answer within 3 minutes it rolls back by itself. The current image is kept for a manual rollback.`, action: 'Upgrade' },
    rollback: { title: 'Roll back to the previous image?', body: `The API goes back to ${s?.previous_image || 'the previous image'} and restarts. Linked phones reconnect.`, action: 'Roll back' },
  };

  const run = () => {
    if (!ask) return;
    op.mutate(
      { op: ask, tag: tag.trim() },
      {
        onSuccess: () => { toast.success(`${confirmCopy[ask].action} finished`); setAsk(null); logs.refetch(); },
        onError: (e) => { setAsk(null); toastError(e, 'The server operation failed'); },
      },
    );
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button type="button" className="flex w-full items-center gap-2 px-4 py-3 text-left" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Server className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-900">Evolution server</span>
        <span className="text-xs text-gray-500">status, logs, restart and upgrade</span>
      </button>
      {open && (
        <div className="space-y-5 border-t px-4 py-4">
          {status.isLoading && <Loading what="server status" />}
          {status.isError && <ErrorNote error={status.error} what="the server status" />}
          {s && (
            <div className="grid gap-4 text-sm md:grid-cols-2">
              <div className="space-y-1">
                <div className="text-xs uppercase text-gray-500">Evolution</div>
                <div>Version {s.evolution?.version || 'unknown'}</div>
                <div className="text-xs text-gray-500">Previous image: {s.previous_image || 'none kept yet'}</div>
                {(s.containers ?? []).map((c) => (
                  <div key={c.service} className="rounded border border-gray-100 px-2 py-1">
                    <span className="font-medium">{c.service}</span>{' '}
                    <span className={c.state === 'running' ? 'text-green-700' : 'text-red-700'}>{c.state}</span>
                    {c.status && <span className="text-gray-500"> ({c.status})</span>}
                    <div className="truncate font-mono text-xs text-gray-500">{c.image}{c.digest ? ` @ ${c.digest.slice(0, 19)}` : ''}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <div className="text-xs uppercase text-gray-500">Host</div>
                <div>Up {uptime(h?.uptime_s)}{load != null && `, load ${load}`}</div>
                <div>Memory: {h?.mem_avail_mb ?? '?'} MB free of {h?.mem_total_mb ?? '?'} MB{h?.swap_used_mb ? `, swap ${h.swap_used_mb} MB` : ''}</div>
                <div>Disk: {h?.disk_free_gb ?? '?'} GB free of {h?.disk_total_gb ?? '?'} GB</div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <select className={`${selectCls} w-28`} value={service} onChange={(e) => setService(e.target.value as 'api' | 'redis')}>
                <option value="api">api</option>
                <option value="redis">redis</option>
              </select>
              <select className={`${selectCls} w-32`} value={tail} onChange={(e) => setTail(Number(e.target.value))}>
                {[200, 500, 2000].map((n) => <option key={n} value={n}>Last {n} lines</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={() => logs.refetch()} disabled={logs.isFetching}><RefreshCw /> Refresh</Button>
            </div>
            {logs.isLoading && <Loading what="server logs" />}
            {logs.isError && <ErrorNote error={logs.error} what="the logs" />}
            {logs.data && (
              <pre ref={box} className="max-h-72 overflow-auto rounded bg-gray-900 p-3 font-mono text-xs leading-relaxed text-gray-100">
                {(logs.data.data?.lines ?? []).join('\n') || 'No log lines returned.'}
              </pre>
            )}
          </div>

          <div className="space-y-3 border-t pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <PermissionButton code={PERMISSIONS.whatsapp.admin} variant="outline" onClick={() => setAsk('restart')}><RotateCw /> Restart API</PermissionButton>
              <PermissionButton code={PERMISSIONS.whatsapp.admin} variant="outline" disabled={!s?.previous_image} onClick={() => setAsk('rollback')}><Undo2 /> Roll back</PermissionButton>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input className="w-48" placeholder="Version, e.g. 2.4.0" value={tag} onChange={(e) => setTag(e.target.value)} />
              <PermissionButton code={PERMISSIONS.whatsapp.admin} disabled={!tagOk} onClick={() => setAsk('upgrade')}><Upload /> Upgrade</PermissionButton>
            </div>
            {tag && !tagOk && <p className="text-xs text-red-600">Use a version like 2.4.0 or 2.4.0-rc3.</p>}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!ask}
        title={ask ? confirmCopy[ask].title : ''}
        body={ask ? confirmCopy[ask].body : ''}
        action={ask ? confirmCopy[ask].action : ''}
        pending={op.isPending}
        onConfirm={run}
        onClose={() => setAsk(null)}
      />
    </div>
  );
};

export default ServerPanel;
